import React, { createContext, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { titleCaseLocationSlug } from "../util/locationSlug";

const CityContext = createContext({
  city: null,
  state: null,
  normalizedLocation: null,
  loading: false,
  error: null,
});

/**
 * CityContext Provider
 * Parses city/state from URL pathname
 * Handles /cities/:state/:city routes
 */
export const CityProvider = ({ children }) => {
  const location = useLocation();
  const [city, setCity] = useState(null);
  const [state, setState] = useState(null);
  const [normalizedLocation, setNormalizedLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(function parseCityFromRoute() {
    const pathname = location.pathname;
    const cityMatch = pathname.match(/^\/cities\/([^/]+)\/([^/]+)$/);

    if (cityMatch) {
      const stateSlug = cityMatch[1];
      const citySlug = cityMatch[2];
      parseCityData(stateSlug, citySlug);
    } else {
      // Not a city route, clear city context
      setCity(null);
      setState(null);
      setNormalizedLocation(null);
      setError(null);
    }
  }, [location.pathname]);

  const parseCityData = (stateSlug, citySlug) => {
    setLoading(true);
    setError(null);

    try {
      const normalizedState = titleCaseLocationSlug(stateSlug);
      const normalizedCity = titleCaseLocationSlug(citySlug);
      const normalized = `${normalizedCity}, ${normalizedState}`;

      setState(normalizedState);
      setCity(normalizedCity);
      setNormalizedLocation(normalized);
    } catch (err) {
      setError("Failed to parse city data");
      console.error("Error parsing city data:", err);
      setCity(null);
      setState(null);
      setNormalizedLocation(null);
    } finally {
      setLoading(false);
    }
  };

  const value = {
    city,
    state,
    normalizedLocation,
    loading,
    error,
  };

  return <CityContext.Provider value={value}>{children}</CityContext.Provider>;
};

export default CityContext;

